import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { SupportIcon, PhoneIcon, MailIcon, PencilIcon } from "@heroicons/react/solid";

import { getHelpAndSupport } from "../../../../utils/api";
import {
    BUTTON_VARIANT_TYPES,
    HELP_AND_SUPPORT_CARD_TYPES,
    NO_ACCESS_TYPES,
    PERMISSIONS,
} from "../../../../utils/constants";

import Alert, { ALERT_TYPE } from "../../../Auth/Alert";
import DashboardContainer from "../../../SharedComponents/DashboardContainer";
import Button from "../../../SharedComponents/Button";
import AccessControl from "../../../SharedComponents/AccessControl";
import useMountStatus from "../../../../hooks/useMountStatus";
import BouncingBalls from "../../../SharedComponents/Loading/BouncingBalls";

function HelpAndSupport() {
    const [alert, setAlert] = useState("");
    const [alertType, setAlertType] = useState(ALERT_TYPE.ERROR);
    const [loading, setLoading] = useState(true);
    const [helpAndSupportCards, setHelpAndSupportCards] = useState([]);

    const navigate = useNavigate();
    const unmounted = useMountStatus();

    const fetchHelpAndSupport = useCallback(async () => {
        setLoading(true);
        try {
            const helpAndSupportResponse = await getHelpAndSupport();
            if (unmounted) return;
            setHelpAndSupportCards(helpAndSupportResponse);
        } catch (err) {
            if (unmounted) return;
            setAlert(err.message);
            setAlertType(ALERT_TYPE.ERROR);
        }
        setLoading(false);
    }, [unmounted]);

    useEffect(() => {
        fetchHelpAndSupport();
    }, [fetchHelpAndSupport]);

    const getAction = (type, content) => {
        switch (type) {
            case HELP_AND_SUPPORT_CARD_TYPES.EMAIL:
                return "mailto:" + content;
            case HELP_AND_SUPPORT_CARD_TYPES.PHONE:
                return "tel:" + content;
            default:
                return content;
        }
    };

    const getIcon = (type) => {
        switch (type) {
            case HELP_AND_SUPPORT_CARD_TYPES.EMAIL:
                return <MailIcon className={"block h-4 w-4 mr-2"} />;
            case HELP_AND_SUPPORT_CARD_TYPES.PHONE:
                return <PhoneIcon className={"block h-4 w-4 mr-2"} />;
            default:
                return <SupportIcon className={"block h-4 w-4 mr-2"} />;
        }
    };

    const renderHelpAndSupportCard = (helpAndSupportCard) => {
        return (
            <div
                key={helpAndSupportCard.type}
                className="bg-white shadow-sm w-full h-full rounded-lg grid grid-flow-row"
            >
                <div className="p-4 flex justify-between">
                    <div className="font-medium text-indigo-900">{helpAndSupportCard.title}</div>
                    <AccessControl
                        requiredPermission={PERMISSIONS.EDIT_HELP_AND_SUPPORT}
                        noAccessType={NO_ACCESS_TYPES.BLANK}
                    >
                        <Button
                            iconPositionLeft
                            icon={<PencilIcon className="block h-3.5 w-3.5 mr-1" />}
                            colour={BUTTON_VARIANT_TYPES.UNCOLOURED}
                            text="Edit"
                            extraStyling="shadow-none text-primary-600 p-0 focus:ring-0 focus:transparent"
                            onClick={() =>
                                navigate("/help-and-support/edit-help-and-support", {
                                    state: {
                                        helpAndSupportCard: helpAndSupportCard,
                                    },
                                })
                            }
                        />
                    </AccessControl>
                </div>
                <div className={"grid grid-flow-row gap-4 p-4 rounded-lg bg-gradient-to-r from-primary-50"}>
                    <div className="text-sm text-indigo-900 break-all">{helpAndSupportCard.content}</div>
                    <div>
                        <Button
                            onClick={() => (window.location = getAction(helpAndSupportCard.type))}
                            text={helpAndSupportCard.title}
                            extraStyling={
                                "text-white focus:ring-inset focus:ring-1 bg-primary-600 hover:bg-primary-700 " +
                                "focus:outline-none focus:ring-primary-600 w-full text-center justify-center"
                            }
                            icon={getIcon(helpAndSupportCard.type)}
                            iconPositionLeft
                        />
                    </div>
                </div>
            </div>
        );
    };

    return (
        <AccessControl requiredPermission={PERMISSIONS.ACCESS_HELP_AND_SUPPORT} noAccessType={NO_ACCESS_TYPES.REDIRECT}>
            <DashboardContainer title={"Help and support"} titleIcon={<SupportIcon className="block h-5 w-5 mr-2" />}>
                <Alert text={alert} type={alertType} active={alert} close={() => setAlert("")} />
                <div className="grid lg:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-4 w-full grid-rows-2">
                    {loading ? (
                        <div className={"col-span-full flex items-center justify-center pt-20 pb-24 w-full"}>
                            <BouncingBalls />
                        </div>
                    ) : (
                        helpAndSupportCards.map((helpAndSupportCard) => renderHelpAndSupportCard(helpAndSupportCard))
                    )}
                </div>
            </DashboardContainer>
        </AccessControl>
    );
}

export default HelpAndSupport;
